import {
  API_TYPES,
  type GetWorkerRequirementsResponse,
  WorkerRequirementDomain,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { type AxiosError } from "axios";
import { isEmpty } from "lodash";
import { useMemo } from "react";

import { type WorkerRequirement } from "./api/types";
import { requirementRelationship, useGetWorkerRequirements } from "./api/useGetWorkerRequirements";
import { isCredentialRequirementForQualification } from "./isCredentialRequirementForQualification";
import { useCredentialRequirementConfiguration } from "./useCredentialRequirementConfiguration";

export function useGetCredentialRequirementsForWorker(
  qualificationNames: string[],
  options: UseGetQueryOptions<GetWorkerRequirementsResponse, AxiosError, WorkerRequirement[]> = {}
) {
  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const { data, ...queryResult } = useGetWorkerRequirements(
    {
      filter: {
        requiredForBooking: true,
        visibleToWorker: true,
        qualifications: qualificationNames,
        "requirement.id": [...credentialRequirementConfiguration],
        domains: [WorkerRequirementDomain.HIRING, WorkerRequirementDomain.FLEX],
      },
      include: [...requirementRelationship, API_TYPES.requirementMappings],
    },
    { ...options, enabled: !isEmpty(credentialRequirementConfiguration) }
  );

  const qualificationNameToCredentialMap = useMemo(() => {
    const map = new Map<string, WorkerRequirement[]>();

    data?.forEach((workerRequirement) => {
      const { qualifications, requirement } = workerRequirement;
      if (!isDefined(requirement?.id)) {
        return;
      }

      qualifications.forEach((qualificationName) => {
        const isCredentialRequirement = isCredentialRequirementForQualification({
          requirement: workerRequirement,
          domains: [WorkerRequirementDomain.HIRING, WorkerRequirementDomain.FLEX],
          qualificationName,
          isBaseCredential: false,
        });

        if (credentialRequirementConfiguration.has(requirement.id) && isCredentialRequirement) {
          const qualificationRequirements: WorkerRequirement[] = map.get(qualificationName) ?? [];

          qualificationRequirements.push(workerRequirement);
          map.set(qualificationName, qualificationRequirements);
        }
      });
    });

    return map;
  }, [data, credentialRequirementConfiguration]);

  return { qualificationNameToCredentialMap, data, ...queryResult };
}
